import Icon from "@chakra-ui/icon";
import { CircularProgress, Flex, Grid, Spacer, Text } from "@chakra-ui/react";
import React from "react";
import { RiCalendarTodoLine } from "react-icons/ri";
import { useSearchParams } from "react-router-dom";
import { GenericError } from "src/components/Feedback/GenericError";
import { Item, SearchSelect } from "src/components/Inputs/SearchSelect";
import { AdminPage } from "src/components/Layout/AdminPage";
import { RemoteDataView } from "src/components/Layout/RemoteDataView";
import { Breadcrumb } from "src/components/Navigation/Breadcrumb";
import { useEnrollmentPeriod } from "src/components/Providers/EnrollmentPeriodProvider";
import { useFormTemplates } from "src/components/Providers/FormTemplateProvider";
import { ClientStorageTypes, SearchAndFilterTypes } from "src/constants";
import {
  getDashboardFormResponsesOverTime,
  getDashboardFormSchoolGradeRank,
  getDashboardFormStatus,
  getDashboardFormSubstatus,
  getGradeConfigByEnrollmentPeriod,
  getSchoolsByEnrollmentPeriod,
} from "src/generated/hooks/form-service/requests";
import { useOrganization } from "src/hooks/useOrganization";
import { useRemoteDataRestQuery } from "src/hooks/useRemoteDataQuery";
import * as breadcrumb from "src/services/breadcrumb";
import { isNullOrUndefined } from "src/services/predicates";
import * as RD from "src/types/remoteData";
import { AdmissionByStatusChart } from "./AdmissionByStatusChart";
import { useAttendance } from "./AttendanceSelector";
import { DashboardPrintStyles } from "./DashboardPrintStyles";
import { EmptyDashboard } from "./EmptyDashboard";
import { ExportButton } from "./ExportButton";
import { Greeting, useGreeting } from "./Greeting";
import { ResponseByStatusChart } from "./ResponseByStatusChart";
import { ResponsesByGrade } from "./ResponsesByGrade";
import { ResponsesByRank } from "./ResponsesByRank";
import { ResponseSubmissionsOverTime } from "./ResponseSubmissionsOverTime";
import { StatusSummary } from "./StatusSummary";

export function Home() {
  const organizationRD = useOrganization();
  const enrollmentPeriodsContext = useEnrollmentPeriod();
  const formTemplatesContext = useFormTemplates();
  const { attendanceSelector, attendance } = useAttendance();

  const [searchParams, setSearchParams] = useSearchParams();
  const formTemplateId = searchParams.get(SearchAndFilterTypes.FormTemplate);

  // Resolve the selected form template from (in priority order):
  // 1. Query string, 2. Local storage, 3. First available template.
  // Then sync the resolved value back to both stores.
  React.useEffect(() => {
    if (!formTemplatesContext.navFormTemplates.hasData()) return;
    const templates = formTemplatesContext.navFormTemplates.data;
    const firstTemplate = templates[0];
    if (firstTemplate === undefined) return;

    const urlFormTemplateId = searchParams.get(
      SearchAndFilterTypes.FormTemplate
    );
    const localFormTemplateId = localStorage.getItem(
      ClientStorageTypes.FormTemplate
    );

    let resolvedId = urlFormTemplateId ?? localFormTemplateId;

    if (!resolvedId || !templates.some((f) => f.id === resolvedId)) {
      resolvedId = firstTemplate.id;
    }

    // Sync local storage
    localStorage.setItem(ClientStorageTypes.FormTemplate, resolvedId);

    if (searchParams.get(SearchAndFilterTypes.FormTemplate) !== resolvedId) {
      searchParams.set(SearchAndFilterTypes.FormTemplate, resolvedId);
      setSearchParams(searchParams, { replace: true });
    }
  }, [formTemplatesContext.navFormTemplates, searchParams, setSearchParams]);

  const selectedFormTemplate: Item | null = React.useMemo(() => {
    if (!formTemplateId) return null;
    if (!formTemplatesContext.navFormTemplates.hasData()) return null;
    const found = formTemplatesContext.navFormTemplates.data.find(
      (f) => f.id === formTemplateId
    );
    return found ? { key: found.id, label: found.name } : null;
  }, [formTemplateId, formTemplatesContext.navFormTemplates]);

  const setSelectedFormTemplate = React.useCallback(
    (item: Item | null) => {
      if (item === null) {
        searchParams.delete(SearchAndFilterTypes.FormTemplate);
        localStorage.removeItem(ClientStorageTypes.FormTemplate);
      } else {
        searchParams.set(SearchAndFilterTypes.FormTemplate, item.key);
        localStorage.setItem(ClientStorageTypes.FormTemplate, item.key);
      }
      setSearchParams(searchParams);
    },
    [searchParams, setSearchParams]
  );

  const formResponsesOverTimeRD = useRemoteDataRestQuery({
    queryKey: [
      "getDashboardFormResponsesOverTime",
      selectedFormTemplate?.key,
      attendance,
      "",
    ],
    queryFn: () =>
      getDashboardFormResponsesOverTime({
        path: {
          formTemplateId: selectedFormTemplate?.key ?? "",
        },
        query: { attendance },
      }).then((response) => response.data),
    enabled: selectedFormTemplate !== null,
  });

  const formSchoolGradeRankRD = useRemoteDataRestQuery({
    queryKey: [
      "getDashboardFormSchoolGradeRank",
      selectedFormTemplate?.key,
      attendance ?? "",
    ],
    queryFn: () =>
      getDashboardFormSchoolGradeRank({
        path: {
          formTemplateId: selectedFormTemplate?.key ?? "",
        },
        query: { attendance },
      }).then((response) => response.data),
    enabled: selectedFormTemplate !== null,
  });

  const formStatusRD = useRemoteDataRestQuery({
    queryKey: [
      "getDashboardFormStatus",
      selectedFormTemplate?.key,
      attendance ?? "",
    ],
    queryFn: () =>
      getDashboardFormStatus({
        path: {
          formTemplateId: selectedFormTemplate?.key ?? "",
        },
        query: { attendance },
      }).then((response) => response.data),
    enabled: selectedFormTemplate !== null,
  });

  const formSubstatusRD = useRemoteDataRestQuery({
    queryKey: [
      "getDashboardFormSubstatus",
      selectedFormTemplate?.key ?? "",
      attendance ?? "",
    ],
    queryFn: () =>
      getDashboardFormSubstatus({
        path: {
          formTemplateId: selectedFormTemplate?.key ?? "",
        },
        query: { attendance },
      }).then((response) => response.data),
    enabled: selectedFormTemplate !== null,
  });

  const schoolsRD = useRemoteDataRestQuery({
    queryKey: [
      getSchoolsByEnrollmentPeriod.name,
      enrollmentPeriodsContext.selectedEnrollmentPeriod?.id,
    ],
    queryFn: () =>
      getSchoolsByEnrollmentPeriod({
        path: {
          enrollmentPeriodId:
            enrollmentPeriodsContext.selectedEnrollmentPeriod?.id ?? "",
        },
      }).then((response) => response.data),
    enabled: enrollmentPeriodsContext.selectedEnrollmentPeriod !== undefined,
  });

  const gradesRD = useRemoteDataRestQuery({
    queryKey: [
      getGradeConfigByEnrollmentPeriod.name,
      enrollmentPeriodsContext.selectedEnrollmentPeriod?.id,
    ],
    queryFn: () =>
      getGradeConfigByEnrollmentPeriod({
        path: {
          enrollmentPeriodId:
            enrollmentPeriodsContext.selectedEnrollmentPeriod?.id ?? "",
        },
      }).then((response) => response.data),
    enabled: enrollmentPeriodsContext.selectedEnrollmentPeriod !== undefined,
  });

  const { hasSchoolSelectionSection, isSchoolRankingEnabled } =
    React.useMemo(() => {
      if (!formTemplatesContext.navFormTemplates.hasData()) {
        return {
          hasSchoolSelectionSection: false,
          isSchoolRankingEnabled: false,
        };
      }
      const schoolRankingSection = formTemplatesContext.navFormTemplates.data
        .find((ft) => ft.id === selectedFormTemplate?.key)
        ?.sections.find((s) => s.schools_ranking_section !== null);

      if (isNullOrUndefined(schoolRankingSection?.schools_ranking_section)) {
        return {
          hasSchoolSelectionSection: false,
          isSchoolRankingEnabled: false,
        };
      }

      return {
        hasSchoolSelectionSection: true,
        isSchoolRankingEnabled:
          schoolRankingSection.schools_ranking_section.ranking_enabled,
      };
    }, [formTemplatesContext.navFormTemplates, selectedFormTemplate?.key]);

  return (
    <RemoteDataView
      remoteData={RD.toTuple(
        enrollmentPeriodsContext.enrollmentPeriods,
        formTemplatesContext.navFormTemplates
      )}
      error={() => <GenericError />}
      loading={<Loading />}
      loadingIndicatorProps={{ height: "100%" }}
      config={{ showDataWhileReloading: false }}
    >
      {([enrollmentPeriods, formTemplates]) => (
        <>
          <DashboardPrintStyles />
          <AdminPage
            breadcrumb={
              <Breadcrumb
                items={breadcrumb.getBreadcrumbsForHome(organizationRD)}
              />
            }
            heading={
              <Flex direction="column" gap="4">
                <Flex gap="2" wrap="wrap" width="full" alignItems="center">
                  <Flex grow="10" data-print-hide>
                    <Greeting />
                  </Flex>
                  <Spacer data-print-hide />
                  {attendanceSelector}
                  <Flex gap="2" alignItems="center" grow="1">
                    <SearchSelect
                      variant="unlocked"
                      leftIcon={
                        <Icon as={RiCalendarTodoLine} color="gray.500" />
                      }
                      placeholder="Select an enrollment period"
                      label={undefined}
                      items={enrollmentPeriods.map((ep) => ({
                        key: ep.id,
                        label: ep.name,
                      }))}
                      selectedItem={
                        enrollmentPeriodsContext.selectedEnrollmentPeriod
                          ? {
                              key: enrollmentPeriodsContext
                                .selectedEnrollmentPeriod.id,
                              label:
                                enrollmentPeriodsContext
                                  .selectedEnrollmentPeriod.name,
                            }
                          : null
                      }
                      onChange={(item) => {
                        if (item) {
                          enrollmentPeriodsContext.setSelectedEnrollmentPeriodId(
                            item?.key
                          );
                        }
                      }}
                      hideClearButton
                    />
                    <SearchSelect
                      variant="unlocked"
                      leftIcon={<></>}
                      placeholder="Select a form"
                      label={undefined}
                      items={formTemplates.map((f) => ({
                        key: f.id,
                        label: f.name,
                      }))}
                      selectedItem={selectedFormTemplate}
                      onChange={setSelectedFormTemplate}
                      hideClearButton
                    />
                    <ExportButton
                      selectedFormTemplate={selectedFormTemplate}
                      hasSchoolSelectionSection={hasSchoolSelectionSection}
                    />
                  </Flex>
                  {selectedFormTemplate === null ? null : (
                    <StatusSummary
                      remoteData={[formStatusRD, formSubstatusRD]}
                      formTemplateId={selectedFormTemplate.key}
                      hasSchoolRankingSection={hasSchoolSelectionSection}
                      attendance={attendance}
                    />
                  )}
                </Flex>
              </Flex>
            }
          >
            {selectedFormTemplate === null ? (
              <EmptyDashboard />
            ) : (
              <Flex sx={{ containerType: "inline-size" }}>
                <Grid
                  gap="4"
                  gridTemplateColumns="repeat(2, 1fr)"
                  gridTemplateAreas={'"one two"'}
                  sx={{
                    "@container (width < 1000px)": {
                      gridTemplateColumns: "1fr",
                      gridTemplateAreas: '"one" "two"',
                      "& > *": { gridColumn: "span 1 !important" },
                    },
                    "@media print": {
                      gridTemplateColumns: "repeat(2, 1fr) !important",
                    },
                  }}
                  width="full"
                >
                  <ResponseByStatusChart
                    formTemplateId={selectedFormTemplate.key}
                    exportId="export-response-by-status"
                    remoteData={[formStatusRD, schoolsRD, gradesRD]}
                  />
                  {hasSchoolSelectionSection ? (
                    <AdmissionByStatusChart
                      formTemplateId={selectedFormTemplate.key}
                      exportId="export-admission-by-status"
                      remoteData={[formSubstatusRD, schoolsRD, gradesRD]}
                    />
                  ) : null}
                  <ResponseSubmissionsOverTime
                    exportId="export-submissions-over-time"
                    remoteData={formResponsesOverTimeRD}
                  />
                  {isSchoolRankingEnabled ? (
                    <ResponsesByRank
                      exportId="export-responses-by-rank"
                      remoteData={[formSchoolGradeRankRD, schoolsRD, gradesRD]}
                    />
                  ) : null}
                  <ResponsesByGrade
                    exportId="export-responses-by-grade"
                    remoteData={[formSchoolGradeRankRD, schoolsRD, gradesRD]}
                    cardProps={
                      !hasSchoolSelectionSection
                        ? {
                            gridArea: "two",
                          }
                        : {}
                    }
                    isSchoolRankingEnabled={isSchoolRankingEnabled}
                  />
                </Grid>
              </Flex>
            )}
          </AdminPage>
        </>
      )}
    </RemoteDataView>
  );
}

const Loading = () => {
  const greeting = useGreeting();
  return (
    <Grid height="100%" placeItems="center">
      <Flex direction="column" alignItems="center" gap="10">
        <CircularProgress isIndeterminate color="primary.500" />
        {greeting
          .map((g) => (
            <Flex direction="column" gap="0" alignItems="center">
              <Text fontSize="xl">{g.title}</Text>
              <Text fontSize="lg" textColor="gray.600" fontWeight="400">
                {g.subtitle}
              </Text>
            </Flex>
          ))
          .withDefault(null)}
      </Flex>
    </Grid>
  );
};
